<script setup>
import gsap from '~/vendors/gsap'
import SplitType from 'split-type'
import GlobalEmitter from '~~/glxp/utils/emitter'
import { useIsMobile } from '~/composables/useIsMobile'
import {
  eyebrowAnimation,
  headlineAnimation,
  bodyCopyAnimation,
  dividerAnimationCenter,
  dividerAnimationLeft,
  mediaCardSlideAnimation,
} from '~~/utils/animations'

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  slug: {
    type: String,
    required: true,
  },
  eyebrow: {
    type: String,
    default: null,
  },
  headline: {
    type: String,
    default: null,
  },
  headlineSize: {
    type: String,
    default: 'h3',
  },
  copy: {
    type: Object,
    default: null,
  },
  gallery: {
    type: Array,
    default: null,
  },
  backgroundImage: {
    type: String,
    default: null,
  },
  backgroundImageMobile: {
    type: String,
    default: null,
  },
  callToAction: {
    type: Object,
    default: null,
  },
  callToActionGtmClass: {
    type: String,
    default: null,
  },
  videoCard: {
    type: Object,
    default: null,
  },
  videoGtmClass: {
    type: String,
    default: null,
  },
  isLast: {
    type: Boolean,
    default: false,
  },
})

/**
 * Contaning component
 */
const el = ref(null)

/**
 * Background image
 */
const isMobile = useIsMobile()
const image = computed(() =>
  isMobile.value && props.backgroundImageMobile
    ? props.backgroundImageMobile
    : props.backgroundImage
)

/**
 * Headline Display
 */
const headlineNum = computed(() => props.headlineSize[1])
const headlineClasses = computed(() => {
  return [
    `text__headline-${headlineNum.value}`,
    props.slug !== 'fleet-coda' && props.slug !== 'coda' && 'text--gradient',
  ]
})

/**
 * Header animation
 */
const header = ref(null)
let timeline
let eyebrowText
function doSplit() {
  if (props.eyebrow && el.value) {
    eyebrowText = new SplitType(
      el.value.querySelector('.home-section__eyebrow'),
      {
        types: 'chars',
      }
    )
  }
}

async function initHeader() {
  await nextTick()
  const headline = el.value.querySelector('.home-section__headline')
  const divider = el.value.querySelector('.home-section__divider')
  const cta = el.value.querySelector('.home-section__call-to-action')
  const mediaCard = el.value.querySelector(
    '.home-section__card-video-container'
  )
  const bodyCopy = el.value.querySelectorAll('.home-section__copy .line')
  const galleryCopy = el.value.querySelectorAll(
    '.home-gallery__item--type-text.home-gallery__item--index-0 .line'
  )

  doSplit()

  if (timeline) {
    timeline.seek(timeline.duration())
    timeline.kill()
  }
  timeline = gsap.timeline({ paused: true, clearProps: true })

  // Eyebrow
  if (eyebrowText) {
    timeline
      .addLabel('eyebrow', 0)
      .from(eyebrowText.chars, eyebrowAnimation, 'eyebrow')
  }

  // Headline
  if (headline) {
    const stInstance0 = new SplitType(headline, { types: 'lines' })
    const { lines: headlineLines } = stInstance0
    timeline
      .addLabel('headline', eyebrowText ? 'eyebrow+=0.25' : 0)
      .from(headlineLines, headlineAnimation, 'headline')
  }

  if (bodyCopy.length) {
    timeline
      .addLabel('bodyCopy', 'headline+=0.5')
      .from(bodyCopy, bodyCopyAnimation, 'bodyCopy')
  }

  if (galleryCopy.length) {
    timeline
      .addLabel('galleryCopy', 'headline+=0.5')
      .from(galleryCopy, bodyCopyAnimation, 'galleryCopy')
  }

  // Divider
  if (divider) {
    const isCentered = divider.classList.contains('horizontal-rule--center')
    timeline.from(
      divider,
      isCentered ? dividerAnimationCenter : dividerAnimationLeft,
      eyebrowText ? 'eyebrow+=0.25' : 'headline+=0.35'
    )
  }

  // CTA
  if (cta) {
    timeline.from(
      cta,
      {
        opacity: 0,
        duration: 1,
        y: 15,
      },
      'headline+=0.75'
    )
  }

  if (mediaCard) {
    timeline.from(mediaCard, mediaCardSlideAnimation, 'headline+=0.2')
  }

  if (isActive.value) {
    timeline.seek(timeline.duration())
  }
}

const isActive = ref(false)

function reveal() {
  isActive.value = true
}

function unreveal() {
  isActive.value = false
}

watch(isActive, (value) => {
  if (value) {
    timeline?.play()
  } else {
    timeline?.reverse()
  }
})

onMounted(() => {
  GlobalEmitter.on('loader_animation_end', initHeader)
  GlobalEmitter.on(`reveal:${props.slug}`, reveal)
  GlobalEmitter.on(`unreveal:${props.slug}`, unreveal)
  GlobalEmitter.on('debouncedResize', initHeader)
})

onBeforeUnmount(() => {
  timeline?.kill()
  GlobalEmitter.off(`reveal:${props.slug}`, reveal)
  GlobalEmitter.off(`unreveal:${props.slug}`, unreveal)
  GlobalEmitter.off('debouncedResize', initHeader)
  GlobalEmitter.off('loader_animation_end', initHeader)
})
</script>

<template>
  <ClippedSection :slug="slug" :backgroundImage="image" :isLast="isLast">
    <section
      class="home-section section"
      :class="[`home-section--${slug}`]"
      :data-slug="slug"
      ref="el"
    >
      <div class="home-section__wrapper">
        <div
          v-if="videoCard"
          class="home-section__card-video-container container"
        >
          <div class="home-section__card-video-inner">
            <CardVideo
              class="home-section__card-video"
              :video-gtm-class="videoGtmClass"
              :title="videoCard.title"
              :video="videoCard.video"
              :thumbnail="videoCard.thumbnail?.url"
              :copy="videoCard.copy"
            />
          </div>
        </div>

        <div class="home-section__inner container">
          <div class="home-section__content">
            <header class="home-section__header">
              <span v-if="eyebrow" class="home-section__eyebrow text__eyebrow">
                {{ eyebrow }}
              </span>
              <HorizontalRule v-if="eyebrow" class="home-section__divider" />
              <h2
                v-if="headline"
                class="home-section__headline"
                :class="headlineClasses"
                v-html="$preventOrphans(headline)"
              />
              <HorizontalRule
                center
                v-if="
                  (!eyebrow && headline && headlineNum < 5 && copy) ||
                  slug === 'coda' ||
                  slug === 'fleet-coda' ||
                  slug === 'coming-soon-to-spaceport-america'
                "
                class="home-section__divider"
              />
            </header>

            <ContentfulRichText
              v-if="copy"
              class="home-section__copy"
              :size="headlineNum > 3 ? 'small' : 'medium'"
              :json="copy.json"
              split
              :sectionSlug="slug"
            />

            <HomeGallery v-if="gallery?.length" :items="gallery" :slug="slug" />

            <div v-if="callToAction" class="home-section__call-to-action">
              <CallToAction
                outline
                icon="chevron-right"
                animate
                :gtm-class="callToActionGtmClass"
                v-bind="callToAction"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  </ClippedSection>
</template>

<style lang="scss">
@import 'styles/vendors/sass-mq/mq';
@import 'styles/utils/functions';

.home-section {
  position: relative;
  min-height: var(--real-height, 100vh);
  width: 100%;

  &__wrapper {
    display: flex;
    width: 100%;
    position: relative;
    z-index: 2;
    justify-content: space-between;
    flex-direction: column;
    min-height: var(--real-height, 100vh);
    padding-top: 97px; // 65px header + 32px margin

    @include mq($from: 'm') using ($from) {
      padding-top: 134px; // 102px header + 32px margin
    }

    @include mq($from: 'l') using ($from) {
      justify-content: flex-start;
      flex-direction: row;
      padding-top: 0;
    }
  }

  &__header {
    margin-bottom: 65px;
    width: gs(7, 6, 'xxxs');

    @include mq($from: 'l') using ($from) {
      width: auto;
    }
  }

  &__headline {
    .line {
      display: inline-block;
    }
  }

  &__inner {
    position: relative;
    z-index: 10;
  }

  &__card-video-container {
    @include mq($from: 'l') using ($from) {
      position: absolute;
      top: 220px;
      width: 100%;
      z-index: 20;
    }
  }

  &__card-video-inner {
    position: relative;
    width: 100%;
  }

  &__card-video {
    @include mq($from: 'l') using ($from) {
      position: absolute;
      top: 0;
      right: gs(2, 2, 'l');
    }
  }

  // styles for individual sections
  &--mission {
    .home-section__content {
      padding-top: var(--clipped-section-angle);
    }
    // glow
    &::after {
      content: '';
      position: absolute;
      top: -25vh;
      right: 0;
      left: 0;

      height: calc(1300px + 50vh);

      background: radial-gradient(
          62.22% 94.81% at 99.13% 25vh,
          rgba(200, 10, 251, 0.15) 0%,
          rgba(200, 10, 251, 0) 100%
        ),
        radial-gradient(
          50.05% 50.05% at 50% 25vh,
          rgba(21, 42, 244, 0.22) 0%,
          rgba(21, 42, 244, 0) 100%
        ),
        linear-gradient(to top, rgba(13, 6, 29, 0) 0%, #0d061d 100%);
      transform: matrix(-1, 0, 0, 1, 0, 0);
    }
    &::before {
      content: '';
      position: absolute;
      top: -25vh;
      right: 0;
      left: 0;
      height: calc(100% + 50vh);
      background: url('~/assets/images/home/bg-stars-repeat.jpg') center top
        repeat;
    }

    .home-section__header {
      @include mq($until: 'l') using ($until) {
        padding-top: 100px;
      }

      @include mq($from: 'l') using ($from) {
        margin-left: gs(5, 5, 'l');
        margin-bottom: 0;
        width: gs(11, 10, 'l');
      }
    }

    .home-gallery {
      // padding-bottom: 25px;
    }
  }
  &--train {
    display: flex;

    background: #000;

    .home-section {
      &__wrapper {
        @include mq($until: 'l') using ($until) {
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
        }
      }

      &__inner {
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        width: 100%;
        padding-bottom: 40px;

        @include mq($from: 'l') using ($from) {
          padding-bottom: 56px;
        }
      }
      &__content {
        display: flex;
        flex-direction: column;
        align-items: center;
      }
      &__header {
        text-align: center;

        @include mq($until: 'l') using ($until) {
          margin-bottom: 24px;

          .text__headline-3 + .horizontal-rule {
            margin-top: 8px;
          }
        }

        @include mq($from: 'l') using ($from) {
          width: gs(12, 11, 'l');
          margin: 0 auto;
        }
      }
      &__copy {
        @include mq($from: 'l') using ($from) {
          text-align: center;
          width: gs(8, 7, 'l');
          margin: 0 auto;
        }
      }
      &__call-to-action {
        margin-top: 40px;
      }
    }
  }

  &--overview-effect,
  &--station,
  &--mechanics-and-tech {
    display: flex;

    .home-section {
      &__inner {
        display: flex;
        width: 100%;
        flex-direction: column;
        justify-content: flex-end;
        padding-bottom: 28px;

        @include mq($from: 'l') using ($from) {
          padding-bottom: 56px;
        }
      }

      &__header {
        margin-bottom: 20px;
      }
      &__content {
        @include mq($from: 'l') using ($from) {
          margin-left: gs(4, 3, 'l');
          width: gs(6, 5, 'l');
        }
      }
      &__copy {
        text-align: left;
      }
      &__call-to-action {
        display: block;
        margin-top: 32px;
      }
    }
  }

  &--coda {
    // overflow: hidden;
    .home-section__headline {
      text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.15),
        0px 1px 20px rgba(146, 107, 144, 0.35), 0px 1px 2px #c9a0af,
        0px 2px 45px rgba(186, 139, 153, 0.9);
      position: relative;
      font-size: 57px;

      &::before {
        content: '';
        position: absolute;
        width: 1285px;
        height: 319px;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);

        background: radial-gradient(
          38.96% 48.32% at 49.35% 50.15%,
          rgba(200, 153, 165, 0.4) 0%,
          rgba(200, 153, 165, 0) 100%
        );
      }
    }
    .home-section__header {
      padding-top: 150px;

      @include mq($until: 'l') using ($until) {
        padding-top: 150px;
        text-align: center;
      }

      @include mq($from: 'l') using ($from) {
        width: gs(12, 11, 'l');
        margin: 0 auto;
        text-align: center;
      }
    }

    .home-section__wrapper {
      background: radial-gradient(
        117.57% 117.57% at 50% 50%,
        rgba(17, 9, 32, 0) 0%,
        rgba(17, 9, 32, 0.3) 100%
      );
      height: var(--real-height, 100vh);
    }

    .horizontal-rule {
      @include mq($until: 'l') using ($until) {
        margin-top: 0;
      }
    }

    .home-section__headline {
      @include mq($until: 'l') using ($until) {
        font-size: 28px;
      }
    }
  }

  // spacecraft fleet
  &--fleet-coda {
    display: flex;
    .home-section {
      &__wrapper {
        min-height: 100%;
      }
      &__inner {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-end;
        text-align: center;
        height: 100%;
      }

      &__header {
        margin-bottom: 10vh;
        @include mq($until: 'l') using($until) {
          .horizontal-rule {
            margin-top: 0;
            width: 88px;
          }
        }
      }
      &__headline {
        text-shadow: 0px 2px 2px rgba(0, 0, 0, 0.16);
      }
    }

    @include mq($from: 'l') using ($from) {
      .horizontal-rule {
        display: none;
      }
    }
  }

  &--watch-vss-unity-descend {
    .home-section {
      &__card-video-container {
        top: auto;
        bottom: 220px;
      }
    }
  }

  // spaceport america
  &--experience {
    .home-section__inner {
      padding-top: var(--clipped-section-angle);
    }
    .home-section__header {
      @include mq($from: 'l') using ($from) {
        margin-left: gs(5, 5, 'l');
        margin-bottom: 0;
        width: gs(11, 10, 'l');
      }
    }
  }

  // &--overview-effect,
  // &--mechanics-and-tech,
  // &--station,
  // &--train,
  // &--spaceflight-with-virgin-galactic {
  //   @include mq($until: 'l') using ($until) {
  //     .home-section__wrapper {
  //       justify-content: flex-end;
  //       flex-direction: column;
  //       height: var(--real-height, 100vh);
  //     }
  //   }
  // }
}

.clipped-section--slug-train .clipped-section__background {
  @include mq($until: 'm') using ($until) {
    img {
      height: 75%;
    }
  }
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transform: matrix(1, 0, 0, -1, 0, 0);

    background: radial-gradient(
        62.22% 94.81% at 99.13% 0%,
        rgba(200, 10, 251, 0.15) 0%,
        rgba(200, 10, 251, 0) 100%
      ),
      radial-gradient(
        50.05% 50.05% at 50% 0%,
        rgba(21, 42, 244, 0.22) 0%,
        rgba(21, 42, 244, 0) 100%
      ),
      radial-gradient(
        100% 41.38% at 50.35% 100%,
        rgba(13, 6, 29, 0) 68.8%,
        #0d061d 100%
      );

    @include mq($from: 'm') using ($from) {
      background: radial-gradient(
            62.22% 94.81% at 99.13% 0%,
            rgba(200, 10, 251, 0.15) 0%,
            rgba(200, 10, 251, 0) 100%
          )
          /* warning: gradient uses a rotation that is not supported by CSS and may not behave as expected */,
        radial-gradient(
          50.05% 50.05% at 50% 0%,
          rgba(21, 42, 244, 0.22) 0%,
          rgba(21, 42, 244, 0) 100%
        ),
        radial-gradient(
          65.71% 41.38% at 50.35% 76.93%,
          rgba(13, 6, 29, 0) 68.8%,
          #0d061d 100%
        );
    }
  }
}
</style>
